
import { db } from './firebase';
import { doc, setDoc, getDoc, collection, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import {
  DashboardBoardLayout,
  ModuleStateEnvelope,
  Task,
  Todo,
  QuickLink,
  ImportantDate,
  SidebarLayout,
  Routine,
  Rule,
  Trade,
} from '../types';

// --- Helper Functions ---

// All data is now stored in a single document for shared access.
const mainDataDocRef = doc(db, 'app-data', 'main');

// Gets the entire data object from the main document.
const getAppData = async () => {
  const docSnap = await getDoc(mainDataDocRef);
  return docSnap.exists() ? docSnap.data() : {};
}

// --- Task (Mission) Actions ---
export const handleSaveTask = async (task: Task, currentTasks: Task[]) => {
  // Sanitize the task object to remove any 'undefined' values, which Firestore prohibits.
  const sanitizedTask = JSON.parse(JSON.stringify(task));

  const safeCurrentTasks = Array.isArray(currentTasks) ? currentTasks : [];
  let updatedTasks;
  if (sanitizedTask.id) { // Edit
    updatedTasks = safeCurrentTasks.map(t => t.id === sanitizedTask.id ? sanitizedTask : t);
  } else { // Create
    const newTask = { ...sanitizedTask, id: crypto.randomUUID(), isCompleted: false };
    updatedTasks = [...safeCurrentTasks, newTask];
  }
  await setDoc(mainDataDocRef, { 'crypto-missions:tasks': updatedTasks }, { merge: true });
};

export const handleToggleComplete = async (taskId: string, currentTasks: Task[]) => {
  const safeCurrentTasks = Array.isArray(currentTasks) ? currentTasks : [];
  const updatedTasks = safeCurrentTasks.map(t =>
    t.id === taskId ? { ...t, isCompleted: !t.isCompleted } : t
  );
  await setDoc(mainDataDocRef, { 'crypto-missions:tasks': updatedTasks }, { merge: true });
};

export const handleDeleteTask = async (taskId: string, currentTasks: Task[]) => {
  const safeCurrentTasks = Array.isArray(currentTasks) ? currentTasks : [];
  const updatedTasks = safeCurrentTasks.filter(t => t.id !== taskId);
  await setDoc(mainDataDocRef, { 'crypto-missions:tasks': updatedTasks }, { merge: true });
};

// --- Todos Actions ---
export const handleAddTodo = async (text: string, currentTodos: Todo[]) => {
  if (text.trim() === '') return;
  const safeCurrentTodos = Array.isArray(currentTodos) ? currentTodos : [];
  const newTodo = { id: crypto.randomUUID(), text, isCompleted: false };
  const updatedTodos = [...safeCurrentTodos, newTodo];
  await setDoc(mainDataDocRef, { 'crypto-missions:todos': updatedTodos }, { merge: true });
};

export const handleToggleTodo = async (todoId: string, currentTodos: Todo[]) => {
  const safeCurrentTodos = Array.isArray(currentTodos) ? currentTodos : [];
  const updatedTodos = safeCurrentTodos.map(t =>
    t.id === todoId ? { ...t, isCompleted: !t.isCompleted } : t
  );
  await setDoc(mainDataDocRef, { 'crypto-missions:todos': updatedTodos }, { merge: true });
};

export const handleDeleteTodo = async (todoId: string, currentTodos: Todo[]) => {
  const safeCurrentTodos = Array.isArray(currentTodos) ? currentTodos : [];
  const updatedTodos = safeCurrentTodos.filter(t => t.id !== todoId);
  await setDoc(mainDataDocRef, { 'crypto-missions:todos': updatedTodos }, { merge: true });
};

// --- Routines Actions ---
export const handleAddRoutine = async (text: string, currentRoutines: Routine[]) => {
  if (text.trim() === '') return;
  const safeCurrentRoutines = Array.isArray(currentRoutines) ? currentRoutines : [];
  const newRoutine = { id: crypto.randomUUID(), text, isCompleted: false };
  const updatedRoutines = [...safeCurrentRoutines, newRoutine];
  await setDoc(mainDataDocRef, { 'crypto-missions:routines': updatedRoutines }, { merge: true });
};

export const handleToggleRoutine = async (routineId: string, currentRoutines: Routine[]) => {
  const safeCurrentRoutines = Array.isArray(currentRoutines) ? currentRoutines : [];
  const updatedRoutines = safeCurrentRoutines.map(t =>
    t.id === routineId ? { ...t, isCompleted: !t.isCompleted } : t
  );
  await setDoc(mainDataDocRef, { 'crypto-missions:routines': updatedRoutines }, { merge: true });
};

export const handleDeleteRoutine = async (routineId: string, currentRoutines: Routine[]) => {
  const safeCurrentRoutines = Array.isArray(currentRoutines) ? currentRoutines : [];
  const updatedRoutines = safeCurrentRoutines.filter(t => t.id !== routineId);
  await setDoc(mainDataDocRef, { 'crypto-missions:routines': updatedRoutines }, { merge: true });
};

// --- Other Actions ---
export const handleNotesChange = async (notes: string) => {
  await setDoc(mainDataDocRef, { 'crypto-missions:notes': notes }, { merge: true });
};

export const handleNotesHeightChange = async (height: string) => {
  await setDoc(mainDataDocRef, { 'crypto-missions:notesHeight': height }, { merge: true });
};

export const handleSaveQuickLink = async (link: Omit<QuickLink, 'id'>, currentLinks: QuickLink[]) => {
  const safeCurrentLinks = Array.isArray(currentLinks) ? currentLinks : [];
  const newLink = { ...link, id: crypto.randomUUID() };
  const updatedLinks = [...safeCurrentLinks, newLink];
  await setDoc(mainDataDocRef, { 'crypto-missions:quickLinks': updatedLinks }, { merge: true });
};

export const handleDeleteQuickLink = async (linkId: string, currentLinks: QuickLink[]) => {
  const safeCurrentLinks = Array.isArray(currentLinks) ? currentLinks : [];
  const updatedLinks = safeCurrentLinks.filter(link => link.id !== linkId);
  await setDoc(mainDataDocRef, { 'crypto-missions:quickLinks': updatedLinks }, { merge: true });
};

export const handleSaveLeaderboard = async (link: Omit<QuickLink, 'id'>, currentLinks: QuickLink[]) => {
  const safeCurrentLinks = Array.isArray(currentLinks) ? currentLinks : [];
  const newLink = { ...link, id: crypto.randomUUID() };
  const updatedLinks = [...safeCurrentLinks, newLink];
  await setDoc(mainDataDocRef, { 'crypto-missions:leaderboards': updatedLinks }, { merge: true });
};

export const handleDeleteLeaderboard = async (linkId: string, currentLinks: QuickLink[]) => {
  const safeCurrentLinks = Array.isArray(currentLinks) ? currentLinks : [];
  const updatedLinks = safeCurrentLinks.filter(link => link.id !== linkId);
  await setDoc(mainDataDocRef, { 'crypto-missions:leaderboards': updatedLinks }, { merge: true });
};

export const handleSaveImportantDate = async (importantDate: Omit<ImportantDate, 'id'>, currentDates: ImportantDate[]) => {
  // `date` 필드는 YYYY-MM-DD 문자열이므로 별도로 Date 객체로 변환할 필요 없이 그대로 사용합니다.
  // Undefined 필드 제거를 위해 spread operator를 사용하거나 직접 객체를 생성하는 것이 더 안전합니다.
  const newImportantDate = { ...importantDate, id: crypto.randomUUID() };
  const safeCurrentDates = Array.isArray(currentDates) ? currentDates : [];
  const updatedDates = [...safeCurrentDates, newImportantDate];
  await setDoc(mainDataDocRef, { 'crypto-missions:importantDates': updatedDates }, { merge: true });
};

export const handleDeleteImportantDate = async (dateId: string, currentDates: ImportantDate[]) => {
  const safeCurrentDates = Array.isArray(currentDates) ? currentDates : [];
  const updatedDates = safeCurrentDates.filter(d => d.id !== dateId);
  await setDoc(mainDataDocRef, { 'crypto-missions:importantDates': updatedDates }, { merge: true });
};

export const handleLayoutChange = async (layout: SidebarLayout) => {
  await setDoc(mainDataDocRef, { 'crypto-missions:sidebarLayout': layout }, { merge: true });
};

export const handleDashboardBoardLayoutChange = async (layout: DashboardBoardLayout) => {
  await setDoc(mainDataDocRef, { 'crypto-missions:dashboardBoardLayout': layout }, { merge: true });
};

export const handleModuleStateChange = async <T>(key: string, envelope: ModuleStateEnvelope<T>) => {
  await setDoc(mainDataDocRef, { [key]: envelope }, { merge: true });
};

// --- Watchlist Actions ---
export const handleSaveWatchlistIds = async (ids: string[]) => {
  await setDoc(mainDataDocRef, { 'crypto-missions:watchlistIds': ids }, { merge: true });
};

// --- Rule Signal Actions ---
export const addRule = async (rule: Omit<Rule, 'id'>): Promise<string> => {
  const sanitized = JSON.parse(JSON.stringify(rule));
  const docRef = await addDoc(collection(db, 'rules'), sanitized);
  return docRef.id;
};

export const updateRule = async (id: string, updates: Partial<Omit<Rule, 'id'>>): Promise<void> => {
  const sanitized = JSON.parse(JSON.stringify(updates));
  await updateDoc(doc(db, 'rules', id), sanitized);
};

export const deleteRule = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, 'rules', id));
};

export const toggleRule = async (id: string, enabled: boolean): Promise<void> => {
  await updateDoc(doc(db, 'rules', id), { enabled });
};

// --- Trade Journal Actions ---
export const addTrade = async (trade: Omit<Trade, 'id'>): Promise<string> => {
  const sanitized = JSON.parse(JSON.stringify(trade));
  const docRef = await addDoc(collection(db, 'trades'), {
    ...sanitized,
    createdAt: Date.now(),
  });
  return docRef.id;
};

export const updateTrade = async (id: string, updates: Partial<Omit<Trade, 'id'>>): Promise<void> => {
  const sanitized = JSON.parse(JSON.stringify(updates));
  await updateDoc(doc(db, 'trades', id), sanitized);
};

export const deleteTrade = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, 'trades', id));
};

export const importData = async (data: any) => {
  const payload: Record<string, unknown> = {
    'crypto-missions:tasks': data.tasks || [],
    'crypto-missions:todos': data.todos || [],
    'crypto-missions:routines': data.routines || [],
    'crypto-missions:notes': data.notes || '',
    'crypto-missions:notesHeight': data.notesHeight || '12rem',
    'crypto-missions:quickLinks': data.quickLinks || [],
    'crypto-missions:leaderboards': data.leaderboards || [],
    'crypto-missions:importantDates': data.importantDates || [],
    'crypto-missions:sidebarLayout': data.sidebarLayout || {},
  };

  if (data.dashboardBoardLayout) {
    payload['crypto-missions:dashboardBoardLayout'] = data.dashboardBoardLayout;
  }

  await setDoc(mainDataDocRef, payload, { merge: true });
}
